import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, homeFor } from "@/lib/auth";
import { fmtErr } from "@/lib/api";
import { isBiometricAvailable, authenticateBiometric, isNative } from "@/lib/native";
import { getToken } from "@/lib/tokenStore";
import Logo from "@/components/Logo";
import { ArrowLeft, Eye, EyeOff, Mail, Lock, Loader2, Fingerprint } from "lucide-react";

const PRESETS = {
  employee: [
    { label: "Employee · Ahmed", email: "employee@enec.ae", password: "Employee@123" },
    { label: "Employee · Fatima", email: "fatima@enec.ae", password: "Employee@123" },
  ],
  driver: [
    { label: "Driver · Mahesh", email: "driver@enec.ae", password: "Driver@123" },
    { label: "Driver · Rajesh", email: "rajesh@enec.ae", password: "Driver@123" },
  ],
  admin: [
    { label: "Super Admin", email: "admin@enec.ae", password: "Admin@123" },
    { label: "Dispatcher · Sara", email: "dispatch@enec.ae", password: "Dispatch@123" },
  ],
};

export default function Login({ defaultRole = "employee" }) {
  const nav = useNavigate();
  const { login, uaePassLogin, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uaeLoading, setUaeLoading] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(true);
  const [bio, setBio] = useState({ available: false });

  useEffect(() => {
    isBiometricAvailable().then(setBio);
  }, []);

  const heading = {
    employee: "Welcome back",
    driver: "Driver Login",
    admin: "Admin Console",
  }[defaultRole];

  const tag = {
    employee: "Sign in with your ENEC credentials",
    driver: "Start your shift, accept rides, drive safe.",
    admin: "Monitor fleet, dispatch and resolve.",
  }[defaultRole];

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, keepLoggedIn);
      toast.success(`مرحباً, ${u.name}`);
      nav(homeFor(u.role), { replace: true });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  }

  async function uaePass() {
    setUaeLoading(true);
    try {
      const u = await uaePassLogin(keepLoggedIn);
      toast.success(`UAE Pass · ${u.name}`);
      nav(homeFor(u.role), { replace: true });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUaeLoading(false);
    }
  }

  async function bioLogin() {
    if (!isNative()) {
      toast.message("Biometric login is available in the native iOS / Android app.", {
        description: "On the web build, please use email + password.",
      });
      return;
    }
    if (!getToken()) {
      toast.info("Sign in once with email & password first — then you can unlock with biometric next time.");
      return;
    }
    try {
      await authenticateBiometric("Unlock TESLAM");
      const u = await refresh();
      if (u) {
        toast.success(`مرحباً, ${u.name}`);
        nav(homeFor(u.role), { replace: true });
      }
    } catch (e) {
      toast.error("Biometric authentication failed");
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Cyan splash */}
      <div className="relative h-64 bg-gradient-to-br from-primary to-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)",
          backgroundSize: "30px 30px, 60px 60px"
        }} />
        <div className="relative h-full px-6 pt-safe flex flex-col justify-between text-white">
          <div className="flex items-center justify-between pt-4">
            <Link to="/" data-testid="login-back" className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25 transition">
              <ArrowLeft size={20} />
            </Link>
            <div className="bg-white rounded-xl p-1.5 shadow-lg">
              <Logo size={36} className="h-9" />
            </div>
          </div>
          <div className="pb-6">
            <p className="text-xs uppercase tracking-widest text-white/70 font-semibold">TESLAM · تسلم</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">{heading}</h1>
            <p className="text-white/80 text-sm mt-1">{tag}</p>
          </div>
        </div>
      </div>

      <div className="-mt-6 mx-4 sm:mx-auto sm:max-w-md w-auto sm:w-full bg-white rounded-3xl border border-slate-200 shadow-xl p-6 relative z-10 flex-1">
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</label>
            <div className="mt-1 relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@enec.ae"
                className="w-full h-14 pl-12 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Password</label>
            <div className="mt-1 relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-14 pl-12 pr-12 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
              />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full h-14 rounded-xl bg-primary hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-lg flex items-center justify-center transition-colors"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : "Sign in"}
          </button>

          <label className="flex items-center justify-between gap-3 px-1 cursor-pointer select-none">
            <span className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                data-testid="login-keep"
                checked={keepLoggedIn}
                onChange={(e) => setKeepLoggedIn(e.target.checked)}
                className="w-4 h-4 accent-primary"
              />
              Keep me signed in
            </span>
            {(bio.available || isNative()) && (
              <button type="button" onClick={bioLogin} data-testid="login-biometric" className="flex items-center gap-1.5 text-primary font-bold uppercase text-[11px] tracking-wider active:scale-95 transition">
                <Fingerprint size={14} /> Biometric
              </button>
            )}
          </label>
        </form>

        {defaultRole === "employee" && (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <button
              data-testid="login-uae-pass"
              onClick={uaePass}
              disabled={uaeLoading}
              className="w-full h-14 rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 text-white font-semibold flex items-center justify-center gap-3 transition-all shadow-md disabled:opacity-60"
            >
              {uaeLoading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded font-bold tracking-wider">UAE</span>
                  Sign in with UAE Pass
                </>
              )}
            </button>
          </>
        )}

        {/* Quick fill */}
        <div className="mt-6 p-3 rounded-xl bg-primary-50 border border-primary-100">
          <p className="text-[11px] uppercase tracking-wider text-primary-700 font-bold">Demo accounts</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {PRESETS[defaultRole].map((p) => (
              <button
                key={p.email}
                type="button"
                data-testid={`preset-${p.email}`}
                onClick={() => {
                  setEmail(p.email);
                  setPassword(p.password);
                }}
                className="text-xs px-3 py-1.5 rounded-lg bg-white border border-primary-200 text-primary-800 hover:bg-primary-100 font-medium transition"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-slate-400 py-6 font-mono">
        Secured by ENEC · Abu Dhabi
      </div>
    </div>
  );
}
